/**
 * Parsed CMS rich text that contains a list of tour steps.
 *
 * Why parse instead of styling the CMS `<ul>/<li>` with CSS?
 * Numbered teal badges alone could be done with CSS counters. We parse because
 * EventTourSteps also needs: (1) an even two-column split at ceil(n/2) — not
 * CSS grid zigzag order — and (2) per-step “See more” truncation with its own
 * expand state, which requires each `<li>` as a Vue item rather than one v-html blob.
 *
 * CMS content often prefixes the list with a `<p>` (intro). That preamble is
 * returned separately so it can render in the same text style as step copy
 * without becoming a numbered step.
 */
export interface ParsedHtmlList {
  /** Meaningful HTML before the list, or null when empty / whitespace-only. */
  preambleHtml: string | null
  items: string[]
}

/**
 * Extract a top-level `<ul>`/`<ol>` and any preceding intro HTML.
 * Returns null when the content is not a list (caller should fall back to raw HTML).
 */
export const parseHtmlList = (html: string): ParsedHtmlList | null => {
  if (!html?.trim()) {
    return null
  }

  const listMatch = html.match(/<(ul|ol)(\s[^>]*)?>([\s\S]*?)<\/\1>/i)
  if (!listMatch?.[3] || listMatch.index === undefined) {
    return null
  }

  const items: string[] = []
  const liRegex = /<li(\s[^>]*)?>([\s\S]*?)<\/li>/gi
  let match: RegExpExecArray | null

  while ((match = liRegex.exec(listMatch[3])) !== null) {
    const inner = match[2]?.trim()
    if (inner) {
      items.push(inner)
    }
  }

  if (items.length === 0) {
    return null
  }

  const preambleRaw = html.slice(0, listMatch.index).trim()
  const preambleHtml =
    preambleRaw && stripHtmlToPlainText(preambleRaw) ? preambleRaw : null

  return { preambleHtml, items }
}

/** Strip tags and decode common entities for length checks / truncated plain text. */
export const stripHtmlToPlainText = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
